import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, CircularProgress, Alert,
    Stack, Divider, Chip
} from '@mui/material';
import { Building, User, MapPin } from 'lucide-react';
import { getRetreatMembers } from '../../../Services/Retreats/RetreatApis';

// ─── Shared primitives ───────────────────────────────────────────────────────

/** Section heading with icon */
const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

/** Label → Value row */
const InfoRow = ({ label, children, noBorder }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start',
            py: 0.9,
            borderBottom: noBorder ? 'none' : '1px solid #F1F5F9',
            gap: { xs: 0.25, sm: 0 },
        }}
    >
        <Typography
            variant="caption"
            sx={{ color: '#94A3B8', fontWeight: 500, minWidth: { sm: 140 }, pt: { sm: '2px' }, flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                    {children}
                </Typography>
            ) : (
                children
            )}
        </Box>
    </Box>
);

/** Thin white card */
const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);

const cardPx = { xs: 2, sm: 2.5 };

const StatusBadge = ({ isActive }) => (
    <Box sx={{
        display: 'inline-block',
        px: 1.2, py: 0.3,
        borderRadius: '4px',
        bgcolor: isActive ? '#E8F5E9' : '#FEECEB',
        color: isActive ? '#2E7D32' : '#F44336',
        fontSize: '0.7rem',
        fontWeight: 600
    }}>
        {isActive ? "Active" : "Inactive"}
    </Box>
);

// ─── Avatar with fallback initials ───────────────────────────────────────────

const Avatar = ({ src, name, size = 60 }) => {
    const initials = (name || '?')
        .split(' ')
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase();

    if (src) {
        return (
            <Box
                component="img"
                loading="lazy"
                src={src}
                alt={name}
                onClick={() => window.open(src, '_blank')}
                sx={{
                    width: size, height: size,
                    borderRadius: '8px',
                    objectFit: 'cover',
                    flexShrink: 0,
                    border: '2px solid #E8EEF4',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.05)' },
                }}
            />
        );
    }
    return (
        <Box
            sx={{
                width: size, height: size,
                borderRadius: '8px',
                bgcolor: '#EFF6FF',
                border: '2px solid #BFDBFE',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1D4ED8', fontSize: size < 40 ? '0.6rem' : '0.8rem' }}>
                {initials}
            </Typography>
        </Box>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const FacilitatorsTab = ({ retreat }) => {
    const [retreatMembers, setRetreatMembers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatMembers(retreat.id);
                if (response?.success) {
                    setRetreatMembers(response.data);
                } else {
                    setError('Failed to fetch facilitator information.');
                }
            } catch (err) {
                console.error('Error fetching retreat members:', err);
                setError('An error occurred while fetching facilitator information.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;
    }

    if (!retreatMembers) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No facilitator information available.
                </Typography>
            </InfoCard>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── 1. Organizer ─────────────────────────────────────────────── */}
            {retreatMembers.organizer && (
                <Box>
                    <SectionTitle icon={Building}>Organizer</SectionTitle>
                    <InfoCard>
                        {/* Header: logo + name */}
                        <Box
                            sx={{
                                px: cardPx,
                                py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid #F1F5F9',
                                flexWrap: 'wrap',
                                gap: 2
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {retreatMembers.organizer.logo ? (
                                    <Box
                                        component="img"
                                        loading="lazy"
                                        src={retreatMembers.organizer.logo}
                                        alt="Organizer Logo"
                                        onClick={() => window.open(retreatMembers.organizer.logo, '_blank')}
                                        sx={{
                                            width: 52, height: 52,
                                            borderRadius: '8px',
                                            objectFit: 'cover',
                                            border: '1px solid #E8EEF4',
                                            flexShrink: 0,
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.05)' },
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 52, height: 52, borderRadius: '8px',
                                            bgcolor: '#FFF7ED', border: '1px solid #FED7AA',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Building size={22} color="#FFBD65" />
                                    </Box>
                                )}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                        {retreatMembers.organizer.name || '—'}
                                    </Typography>
                                    {retreatMembers.organizer.location && (
                                        <Typography variant="caption" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <MapPin size={11} /> {retreatMembers.organizer.location}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            {retreatMembers.organizer.isActive !== undefined && (
                                <StatusBadge isActive={retreatMembers.organizer.isActive} />
                            )}
                        </Box>

                        {/* Contact info rows */}
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            <InfoRow label="Email">
                                {retreatMembers.organizer.email ? (
                                    <Typography
                                        component="a"
                                        href={`mailto:${retreatMembers.organizer.email}`}
                                        variant="body2"
                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        {retreatMembers.organizer.email}
                                    </Typography>
                                ) : '—'}
                            </InfoRow>
                            <InfoRow label="Phone">
                                {retreatMembers.organizer.countryCode + " " + retreatMembers.organizer.phone || '—'}
                            </InfoRow>
                            {retreatMembers.organizer.website && (
                                <InfoRow label="Website">
                                    <Typography
                                        component="a"
                                        href={retreatMembers.organizer.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        {retreatMembers.organizer.website}
                                    </Typography>
                                </InfoRow>
                            )}
                            {retreatMembers.organizer.tripAdvisorUrl && (
                                <InfoRow label="TripAdvisor">
                                    <Typography
                                        component="a"
                                        href={retreatMembers.organizer.tripAdvisorUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        View on TripAdvisor
                                    </Typography>
                                </InfoRow>
                            )}
                            {retreatMembers.organizer.mapIframe && (
                                <InfoRow label="Location Map">
                                    <Typography
                                        component="a"
                                        href={retreatMembers.organizer.mapIframe.match(/src=["']([^"']+)["']/i) ? retreatMembers.organizer.mapIframe.match(/src=["']([^"']+)["']/i)[1] : retreatMembers.organizer.mapIframe}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        View on Map
                                    </Typography>
                                </InfoRow>
                            )}
                            {retreatMembers.organizer.description && (
                                <InfoRow label="Description" noBorder>
                                    <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                                        {retreatMembers.organizer.description}
                                    </Typography>
                                </InfoRow>
                            )}
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 2. Retreat Center ─────────────────────────────────────────── */}
            {retreatMembers.center && (
                <Box>
                    <SectionTitle icon={MapPin}>Retreat Center</SectionTitle>
                    <InfoCard>
                        {/* Center name header */}
                        <Box sx={{ px: cardPx, py: 1.5, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {retreatMembers.center.images?.length > 0 ? (
                                    <Box component="img" loading="lazy" src={retreatMembers.center.images[0].image}
                                        alt={retreatMembers.center.name}
                                        onClick={() => window.open(retreatMembers.center.images[0].image, '_blank')}
                                        sx={{
                                            width: 52, height: 52, borderRadius: '8px',
                                            objectFit: 'cover', border: '1px solid #E8EEF4', flexShrink: 0,
                                            cursor: 'pointer', transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.05)' },
                                        }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: 52, height: 52, borderRadius: '8px',
                                        bgcolor: '#F0FDF4', border: '1px solid #BBF7D0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}
                                    >
                                        <MapPin size={20} color="#16A34A" />
                                    </Box>
                                )}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                        {retreatMembers.center.name || '—'}
                                    </Typography>
                                    {retreatMembers.center.location && (
                                        <Typography variant="caption" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <MapPin size={11} /> {retreatMembers.center.location}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            {retreatMembers.center.isActive !== undefined && (
                                <StatusBadge isActive={retreatMembers.center.isActive} />
                            )}
                        </Box>

                        {/* Center contact rows */}
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            <InfoRow label="Email">
                                {retreatMembers.center.email ? (
                                    <Typography component="a" href={`mailto:${retreatMembers.center.email}`} variant="body2"
                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        {retreatMembers.center.email}
                                    </Typography>
                                ) : '—'}
                            </InfoRow>
                            <InfoRow label="Phone">
                                {retreatMembers.center.countryCode + " " + retreatMembers.center.phone || '—'}
                            </InfoRow>
                            {retreatMembers.center.website && (
                                <InfoRow label="Website">
                                    <Typography component="a" href={retreatMembers.center.website} target="_blank" rel="noopener noreferrer" variant="body2"
                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        {retreatMembers.center.website}
                                    </Typography>
                                </InfoRow>
                            )}
                            {retreatMembers.center.mapIframe && (
                                <InfoRow label="Location Map">
                                    <Typography component="a" href={retreatMembers.center.mapIframe.match(/src=["']([^"']+)["']/i) ? retreatMembers.center.mapIframe.match(/src=["']([^"']+)["']/i)[1] : retreatMembers.center.mapIframe} target="_blank" rel="noopener noreferrer" variant="body2"
                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        View on Map
                                    </Typography>
                                </InfoRow>
                            )}
                            {retreatMembers.center.description && (
                                <InfoRow label="Description" noBorder={!retreatMembers.center.images?.length}>
                                    <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                                        {retreatMembers.center.description}
                                    </Typography>
                                </InfoRow>
                            )}
                        </Box>

                        {/* Center gallery — remaining images */}
                        {retreatMembers.center.images?.length > 1 && (
                            <Box sx={{ borderTop: '1px solid #F1F5F9', px: cardPx, py: 1.5 }}>
                                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                                    Gallery
                                </Typography>
                                <Grid container spacing={1}>
                                    {retreatMembers.center.images.slice(1).map((img) => (
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={img.id}>
                                            <Box component="img" loading="lazy" src={img.image} alt="Center"
                                                onClick={() => window.open(img.image, '_blank')}
                                                sx={{
                                                    width: '100%', height: 80, objectFit: 'cover', borderRadius: '6px',
                                                    border: '1px solid #E8EEF4', cursor: 'pointer',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </InfoCard>
                </Box>
            )}

            {/* ── 3. Teachers & Facilitators ───────────────────────────────── */}
            {retreatMembers.members?.length > 0 && (
                <Box>
                    <SectionTitle icon={User}>Teachers & Facilitators</SectionTitle>
                    <Grid container spacing={2}>
                        {retreatMembers.members.map((member) => {
                            const teacher = member.teacher || member;
                            const bioContent = teacher?.aboutUs || teacher?.bio;
                            return (
                                <Grid size={{ xs: 12, sm: 6, md: 12 }} key={member.id}>
                                    <InfoCard sx={{ height: '100%' }}>
                                        {/* Profile header */}
                                        <Box
                                            sx={{
                                                px: cardPx, py: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justify: 'space-between',
                                                borderBottom: '1px solid #F1F5F9',
                                                flexWrap: 'wrap',
                                                gap: 1.5
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar
                                                    src={teacher?.profileImage}
                                                    name={teacher?.name}
                                                    size={44}
                                                />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {teacher?.name || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>
                                                        {teacher?.title || 'Teacher'}
                                                    </Typography>
                                                    {teacher?.location && (
                                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <MapPin size={11} /> {teacher.location}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            {teacher?.isActive !== undefined && (
                                                <StatusBadge isActive={teacher.isActive} />
                                            )}
                                        </Box>

                                        {/* Contact + details */}
                                        <Box sx={{ px: cardPx, py: 1.5 }}>
                                            <InfoRow label="Email">
                                                {teacher?.email ? (
                                                    <Typography
                                                        component="a"
                                                        href={`mailto:${teacher.email}`}
                                                        variant="body2"
                                                        sx={{
                                                            color: '#1D4ED8', textDecoration: 'none', fontWeight: 500,
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                                                            '&:hover': { textDecoration: 'underline' },
                                                        }}
                                                    >
                                                        {teacher.email}
                                                    </Typography>
                                                ) : '—'}
                                            </InfoRow>

                                            {teacher?.yogaAlliance && (
                                                <InfoRow label="Certification">
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                                                        {teacher.yogaAlliance}
                                                    </Typography>
                                                </InfoRow>
                                            )}

                                            {teacher?.website && (
                                                <InfoRow label="Website">
                                                    <Typography
                                                        component="a"
                                                        href={teacher.website}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        variant="body2"
                                                        sx={{ color: '#1D4ED8', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                                    >
                                                        {teacher.website}
                                                    </Typography>
                                                </InfoRow>
                                            )}

                                            {teacher?.languages && (
                                                <InfoRow label="Languages">
                                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                        {(Array.isArray(teacher.languages)
                                                            ? teacher.languages
                                                            : teacher.languages.split(',')
                                                        ).map((lang, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={lang.trim()}
                                                                size="small"
                                                                sx={{ height: 18, fontSize: '0.68rem', fontWeight: 500, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', color: '#475569' }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </InfoRow>
                                            )}

                                            {bioContent && (
                                                <>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: '#64748B',
                                                            lineHeight: 1.6,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 6,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: bioContent }}
                                                    />
                                                </>
                                            )}
                                        </Box>
                                    </InfoCard>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

        </Box>
    );
};

export default FacilitatorsTab;

